import React from "react";
import { Controller, Control, RegisterOptions, useFormContext } from "react-hook-form";
import {
  DatePickerView,
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import dateFns from "@date-io/date-fns";
import styled from "styled-components";
import FontStyles from "../../styles/fontStyles";
import {
  createMuiTheme,
  FormControl,
  FormHelperText,
  Theme,
  ThemeProvider,
} from "@material-ui/core";
import brLocate from "date-fns/locale/pt-BR";
import { Colors, fonts, Spacing } from "../../../config";
import colors from "../../../config/colors";

const customTheme: Theme = createMuiTheme({
  overrides: {
    MuiPickersToolbar: {
      toolbar: {
        backgroundColor: colors.black,
        borderBottom: "1px solid #24272A",
        [`@media (max-width:${581 - 1}px)`]: {
          height: Spacing(6.25),
        },
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: Colors.inputGray,
        marginTop: "2vh",
        height: 48,
        fontFamily: fonts.medium,
        fontSize: fonts.sizes.regular,
        "&:hover": {
          backgroundColor: `${Colors.inputGray} !important`,
        },
        "&$focused": { backgroundColor: "white" },
        borderRadius: "21px !important",
      },
      input: {
        padding: "0px",
        paddingLeft:   '10px'
      },
    },
    MuiIconButton: {
      root: {
        color: Colors.black,
        paddingInline: "0px",
      },
    },
    MuiPickersToolbarText: {
      toolbarTxt: { fontFamily: fonts.regular },
      toolbarBtnSelected: { color: "white" },
    },
    MuiPickersCalendarHeader: {
      iconButton: {
        backgroundColor: "transparent",
        color: colors.black,
        [`@media (max-width:${581 - 1}px)`]: {
          marginInline: Spacing(1.5),
        },
      },
      dayLabel: {
        fontFamily: fonts.regular,
        color: colors.black,
        [`@media (max-width:${581 - 1}px)`]: { fontSize: Spacing(1) },
      },
    },
    MuiTypography: {
      alignCenter: {
        fontFamily: fonts.regular,
        color: colors.black,
        [`@media (max-width:${581 - 1}px)`]: { fontSize: Spacing(1.5) },
      },
      body2: {
        fontFamily: fonts.regular,
        [`@media (max-width:${581 - 1}px)`]: { fontSize: Spacing(1.5) },
      },
    },
    MuiPickersBasePicker: {
      pickerView: {
        backgroundColor: colors.inputGray,
        [`@media (max-width:${581 - 1}px)`]: { minHeight: "300px" },
      },
    },
    MuiPickersDay: {
      day: { color: colors.black },
      daySelected: {
        backgroundColor: colors.blue,
        color: colors.whiteBackground,
        "&:hover": {
          backgroundColor: colors.black + "E6",
          color: "white",
          border: "1px solid #24272A",
        },
      },
      current: { color: "#3f51b5" },
    },
    MuiDialogActions: {
      root: {
        backgroundColor: colors.black,
        [`@media (max-width:${581 - 1}px)`]: {
          padding: Spacing(0, 1, 1, 0),
        },
      },
    },
    MuiButton: {
      root: { minWidth: "32px" },
      label: {
        color: colors.whiteBackground,
        fontFamily: fonts.bold,
        [`@media (max-width:${581 - 1}px)`]: {
          fontSize: Spacing(1.4),
          width: "auto",
        },
      },
      text: {
        [`@media (max-width:${581 - 1}px)`]: { padding: Spacing(0) },
      },
    },
    MuiPickersYear: {
      root: { fontFamily: fonts.regular },
      yearSelected: { color: colors.black },
    },
    MuiPickersMonth: {
      monthSelected: { color: colors.black },
    },
  },
} as any);

const Label = styled.p(({ theme, $witherror }: any) => {
  const { palette: colors } = theme;
  return {
    ...FontStyles.semibold14,
    textTransform: "capitalize",
    color: $witherror ? colors.error.main : "#0C396D",
    transition: ".2s",
    pointerEvents: "none",
    fontSize: "12px",
    margin: "0px",
  };
});

const ErrorMessage = styled(FormHelperText)(() => ({
  color: "red",
  minHeight: Spacing(2.5),
}));

// Normalize value to avoid timezone issues
const normalizeValue = (val: any) => {
  if (!val) return null;
  if (typeof val === "string") {
    const dateMatch = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return new Date(
        parseInt(dateMatch[1], 10),
        parseInt(dateMatch[2], 10) - 1,
        parseInt(dateMatch[3], 10)
      );
    }
  }
  if (val instanceof Date) return val;
  return val;
};

export interface InputDateRHFProps {
  control: Control<any>;
  name: string;
  label?: string;
  minDate?: any;
  maxDate?: any;
  disabled?: boolean;
  readOnly?: boolean;
  rules?: RegisterOptions;
  onDateChange?: (value: Date | null) => void;
  onFieldBlur?: () => void;
  views?: DatePickerView[];
  format?: string;
  autoOk?: boolean;
  disabledError?: boolean;
}

function InputDateRHF({
  control,
  name,
  label,
  minDate,
  maxDate,
  disabled,
  readOnly,
  rules,
  onDateChange,
  onFieldBlur,
  views = ["year", "month", "date"],
  format,
  autoOk = true, disabledError
}: InputDateRHFProps) {
  const formContext = useFormContext();
  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field, fieldState }) => {
        const errorMsg = fieldState.error?.message;
        const normalizedValue = normalizeValue(field.value);

        return (
          <FormControl fullWidth error={Boolean(errorMsg)}>
            <MuiPickersUtilsProvider locale={brLocate} utils={dateFns}>
              {label && (
                <Label $witherror={Boolean(errorMsg) ? true : undefined}>
                  {label}
                </Label>
              )}
              <ThemeProvider theme={customTheme}>
                <KeyboardDatePicker
                  {...(readOnly ? { open: !readOnly } : {})}
                  inputVariant="filled"
                  format={format ?? "dd/MM/yyyy"}
                  minDate={
                    typeof minDate !== "string" && minDate !== null
                      ? minDate
                      : undefined
                  }
                  maxDate={
                    typeof maxDate !== "string" && maxDate !== null
                      ? maxDate
                      : undefined
                  }
                  InputProps={{
                    disableUnderline: true,
                    readOnly: readOnly,
                    style: { fontSize: "14px" },
                  }}
                  style={{ marginTop: "-1%" }}
                  value={normalizedValue ?? null}
                  variant={undefined}
                  fullWidth
                  views={views}
                  autoOk={autoOk}
                  disabled={disabled}
                  onChange={(date) => {
                    field.onChange(date);
                    if (onDateChange) onDateChange(date);
                    if (fieldState.invalid) formContext?.trigger(name);
                    if (onFieldBlur) {
                      setTimeout(() => onFieldBlur(), 10);
                    }
                  }}
                  onBlur={() => {
                    field.onBlur();
                    formContext?.trigger(name);
                    if (onFieldBlur) onFieldBlur();
                  }}
                  error={Boolean(errorMsg)}
                  helperText={null}
                />
              </ThemeProvider>
            </MuiPickersUtilsProvider>
            {!disabledError && <ErrorMessage error={Boolean(errorMsg)}>{errorMsg}</ErrorMessage>}
          </FormControl>
        );
      }}
    />
  );
}

export default InputDateRHF;
